import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/modules/api";
import { Uuid } from "@/domains/global/identifiers";
import { atlasRoutes } from "@/app/router/routes";
import {
  ImportStatus,
  ImportStatuses,
  ImportStatusLabels,
} from "@/pages/imports/shared/import-status";
import { Button } from "@radix-ui/themes";
import { SessionEventSummarySchema } from "@/domains/openapi/types";

export const ImportsViewPage: FC = () => {
  const { importSessionId } = useParams<{ importSessionId: Uuid }>();
  const navigate = useNavigate();

  if (!importSessionId) {
    return <div>Invalid ID</div>;
  }

  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: ["import-session-by-id", importSessionId],
    queryFn: () =>
      api.get("/internal/v1/import-sessions/{session_id}", {
        params: {
          path: {
            session_id: importSessionId,
          },
        },
      }),
    refetchOnWindowFocus: true,
  });

  // ---------------------------
  // Questions (INPUT_REQUESTED events) for this session
  // ---------------------------
  const questionsResult = useQuery({
    queryKey: ["session-events", importSessionId, "INPUT_REQUESTED"],
    queryFn: () =>
      api.post("/internal/v1/session-events/search", {
        body: {
          filter_by_session_id: importSessionId,
          filter_by_event_kind: "INPUT_REQUESTED",
          limit: 50,
        },
      }),
    refetchOnWindowFocus: true,
  });

  // ---------------------------
  // Provided inputs (INPUT_PROVIDED events) for this session
  // ---------------------------
  const providedResult = useQuery({
    queryKey: ["session-events", importSessionId, "INPUT_PROVIDED"],
    queryFn: () =>
      api.post("/internal/v1/session-events/search", {
        body: {
          filter_by_session_id: importSessionId,
          filter_by_event_kind: "INPUT_PROVIDED",
          limit: 50,
        },
      }),
    refetchOnWindowFocus: true,
  });

  // Expose imported counts from uploaded_file_batch on the session detail

  // ---------------------------
  // Update Status Mutation
  // ---------------------------
  const updateStatusMutation = useMutation({
    mutationFn: (newStatus: ImportStatus) =>
      api.patch("/internal/v1/import-sessions/{session_id}", {
        params: {
          path: {
            session_id: importSessionId,
          },
        },
        body: {
          status: newStatus,
        },
      }),
    onSuccess: () => {
      // Refresh this session detail + sessions list queries
      queryClient.invalidateQueries({ queryKey: ["import-session-by-id", importSessionId] });
      queryClient.invalidateQueries({ queryKey: ["import-sessions"], exact: false });
      setIsEditing(false);
    },
  });

  const [selectedStatus, setSelectedStatus] = useState<ImportStatus | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Keep local state in sync with query result
  useEffect(() => {
    if (result.data?.data?.status) {
      setSelectedStatus(result.data.data.status as unknown as ImportStatus);
    }
  }, [result.data?.data?.status]);

  if (result.isLoading) {
    return <div>Loading...</div>;
  }

  if (result.isError) {
    return <div>Error loading import session.</div>;
  }

  return (
    <div>
      <h1>View Import Session</h1>
      <div style={{ marginBottom: 8 }}>
        {(() => {
          const batchUnknown = result.data?.data?.uploaded_file_batch as unknown as
            | { imported_note_count?: number; imported_collection_count?: number }
            | undefined;
          const noteCount = batchUnknown?.imported_note_count ?? 0;
          const collectionCount = batchUnknown?.imported_collection_count ?? 0;
          return (
            <>
              <strong>Imported Notes:</strong> {noteCount}
              &nbsp; | &nbsp;
              <strong>Imported Collections:</strong> {collectionCount}
            </>
          );
        })()}
      </div>
      <div>
        <Button
          variant="outline"
          onClick={() =>
            navigate(atlasRoutes.sessionsView({ params: { sessionId: importSessionId } }).path)
          }
        >
          View Session
        </Button>
      </div>
      <div>
        {!isEditing && (
          <>
            <span>Status: {result.data?.data?.status}</span>&nbsp;
            <button onClick={() => setIsEditing(true)}>(Update)</button>
          </>
        )}
      </div>

      {/* Session Questions + Awaiting User Indicator (event-based) */}
      <div style={{ marginTop: 12 }}>
        <h2>Session Questions</h2>
        {questionsResult.isLoading || providedResult.isLoading ? (
          <div>Loading questions...</div>
        ) : (
          (() => {
            const requested: SessionEventSummarySchema[] =
              questionsResult.data?.data?.results ?? [];
            const provided: SessionEventSummarySchema[] = providedResult.data?.data?.results ?? [];

            if (requested.length === 0) {
              return <div>No questions sent.</div>;
            }

            const byTimeAsc = (a: SessionEventSummarySchema, b: SessionEventSummarySchema) =>
              new Date(a.internal_created_at).getTime() - new Date(b.internal_created_at).getTime();

            const requestedSorted = [...requested].sort(byTimeAsc);
            const providedSorted = [...provided].sort(byTimeAsc);

            // Pair FIFO: each INPUT_PROVIDED answers the oldest outstanding INPUT_REQUESTED
            const unansweredQueue: SessionEventSummarySchema[] = [];
            let i = 0;
            let j = 0;
            while (i < requestedSorted.length || j < providedSorted.length) {
              const nextRequestedAt =
                i < requestedSorted.length
                  ? new Date(requestedSorted[i].internal_created_at).getTime()
                  : Number.POSITIVE_INFINITY;
              const nextProvidedAt =
                j < providedSorted.length
                  ? new Date(providedSorted[j].internal_created_at).getTime()
                  : Number.POSITIVE_INFINITY;

              if (nextRequestedAt <= nextProvidedAt) {
                unansweredQueue.push(requestedSorted[i]);
                i += 1;
              } else {
                if (unansweredQueue.length > 0) {
                  unansweredQueue.shift();
                }
                j += 1;
              }
            }

            const isAwaiting = unansweredQueue.length > 0;

            const getInputKind = (event: SessionEventSummarySchema): string => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const value: any = (event as unknown as { data?: { value?: unknown } }).data?.value;
              const inputKind = value?.input_kind;
              return typeof inputKind === "string" && inputKind.length > 0 ? inputKind : "unknown";
            };

            return (
              <>
                <div>
                  <strong>Awaiting User Input:</strong> {isAwaiting ? "Yes" : "No"}
                </div>
                {unansweredQueue.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <strong>Unanswered Questions:</strong>
                    <ul>
                      {unansweredQueue.map(q => (
                        <li key={`unanswered-${q.id}`}>{getInputKind(q)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <strong>All Questions Sent:</strong>
                  <ul>
                    {requestedSorted.map(q => (
                      <li key={q.id}>{getInputKind(q)}</li>
                    ))}
                  </ul>
                </div>
              </>
            );
          })()
        )}
      </div>
      {/* End Session Questions */}

      {isEditing && (
        <>
          {/* Status Dropdown */}
          <label htmlFor="status-select">Status:&nbsp;</label>
          <select
            id="status-select"
            value={selectedStatus ?? ""}
            onChange={e =>
              setSelectedStatus(e.target.value === "" ? null : (e.target.value as ImportStatus))
            }
          >
            {ImportStatuses.map(status => (
              <option key={status} value={status}>
                {ImportStatusLabels[status]}
              </option>
            ))}
          </select>
          &nbsp;
          <button
            onClick={() => selectedStatus && updateStatusMutation.mutate(selectedStatus)}
            disabled={
              updateStatusMutation.status === "pending" ||
              selectedStatus === result.data?.data?.status
            }
          >
            {updateStatusMutation.status === "pending" ? "Updating..." : "Save"}
          </button>
          &nbsp;
          <button
            onClick={() => setIsEditing(false)}
            disabled={updateStatusMutation.status === "pending"}
          >
            Cancel
          </button>
          <br />
        </>
      )}
      <a
        href={`https://cloud.temporal.io/namespaces/prod-backend-async-worker.rqp4d/workflows/ProcessUploadedFileBatchWorkflow-${result.data?.data?.uploaded_file_batch?.id}/history`}
      >
        Temporal Workflow
      </a>
      <pre>{JSON.stringify(result.data, null, 2)}</pre>
    </div>
  );
};
